/**
 * Review platform types — mirrors backend DTOs / Prisma enums for
 * `apps/operations/src/modules/review-platforms/*`.
 */

// ─── Enums (mirror Prisma) ────────────────────────────────────────────────────

export type ReviewPlatform = 'GOOGLE' | 'FACEBOOK' | 'YELP' | 'BBB' | 'THUMBTACK';

/**
 * The platforms this product offers to connect.
 *
 * Deliberately narrower than `ReviewPlatform`. BBB was dropped 2026-08-29 — we
 * do not have it — but the enum member, its capability entry and its label maps
 * all stay: the backend enum still has `BBB`, so a tenant with a stored BBB
 * config must keep rendering as "Better Business Bureau" rather than falling
 * back to a raw enum string. Removing an offer is not the same as erasing the
 * data, and only the offer is gone.
 */
export const REVIEW_PLATFORMS: ReviewPlatform[] = ['GOOGLE', 'FACEBOOK', 'YELP', 'THUMBTACK'];

export type PlatformConnectionStatus = 'CONNECTED' | 'DISCONNECTED' | 'NEEDS_REAUTH' | 'ERROR';

export type PlatformAuthType = 'OAUTH' | 'URL';

export type OAuthProvider = 'GOOGLE_BUSINESS_PROFILE' | 'FACEBOOK_PAGES';

export type OAuthTokenStatus = 'ACTIVE' | 'NEEDS_REAUTH' | 'REVOKED';

// ─── Capability map ──────────────────────────────────────────────────────────

export interface PlatformCapability {
  authType: PlatformAuthType;
  /** OAuth provider URL slug used by the backend `/oauth/:platform/url`. */
  oauthSlug?: 'google' | 'facebook';
  read: boolean;
  /** Reply via API. */
  write: boolean;
  label: string;
  description: string;
  /** Default sync frequency hours used in copy. */
  syncFrequencyHours: number;
}

export const PLATFORM_CAPABILITIES: Record<ReviewPlatform, PlatformCapability> = {
  GOOGLE: {
    authType: 'OAUTH',
    oauthSlug: 'google',
    read: true,
    write: true,
    label: 'Google Business Profile',
    description:
      'Read and reply to Google reviews. Polls every 6h; supports Pub/Sub on partner tier.',
    syncFrequencyHours: 6,
  },
  FACEBOOK: {
    authType: 'OAUTH',
    oauthSlug: 'facebook',
    read: true,
    write: true,
    label: 'Facebook Pages',
    description: 'Graph API — read recommendations and post replies.',
    syncFrequencyHours: 6,
  },
  YELP: {
    authType: 'URL',
    read: true,
    write: false,
    label: 'Yelp',
    description: 'Latest 3 reviews via Fusion API. Replies must be posted on Yelp.',
    syncFrequencyHours: 24,
  },
  // Not in `REVIEW_PLATFORMS` — kept so a stored BBB config still renders with
  // a human label. See the note on that constant.
  BBB: {
    authType: 'URL',
    read: false,
    write: false,
    label: 'Better Business Bureau',
    description: 'Deep-link only. No public API for reading or replying.',
    syncFrequencyHours: 0,
  },
  THUMBTACK: {
    authType: 'URL',
    read: false,
    write: false,
    label: 'Thumbtack',
    description: 'Deep-link only. Pro API is partner-gated and out of scope for v1.',
    syncFrequencyHours: 0,
  },
};

/**
 * What the platforms screen should show a given tenant: everything we offer,
 * plus anything they already have stored that we no longer do.
 *
 * Rendering `REVIEW_PLATFORMS` alone was wrong in a way the docblock above
 * claims it is not. Dropping BBB from the offer left a tenant holding a BBB
 * config with no card at all — nothing to inspect, nothing to disconnect,
 * while the backend still serves the row and still treats it as connectable —
 * and it let the summary read "5 of 4", because the numerator came from the
 * API and the denominator from this list. Removing an offer is not the same as
 * erasing the data, and this is the function that has to mean it.
 *
 * Offered platforms keep their order; anything retired lands after them, in
 * whatever order the API returned. Raised in review on #2689.
 */
export function visiblePlatformsFor(
  configs: ReadonlyArray<{ platform: ReviewPlatform }>,
): ReviewPlatform[] {
  const offered = new Set<ReviewPlatform>(REVIEW_PLATFORMS);
  const retained: ReviewPlatform[] = [];
  for (const config of configs) {
    if (offered.has(config.platform) || retained.includes(config.platform)) continue;
    retained.push(config.platform);
  }
  return [...REVIEW_PLATFORMS, ...retained];
}

export const DEFAULT_PLATFORM_PRIORITY: ReviewPlatform[] = [
  'GOOGLE',
  'FACEBOOK',
  'YELP',
  'THUMBTACK',
];

// ─── Resource shapes ─────────────────────────────────────────────────────────

export interface ReviewSettingsRecord {
  id: string;
  tenantId: string;
  locationId: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * Auto-post kill switch (NOT-576 §4.3). Non-null = everything pending is
   * stopped, across every platform, without disturbing any per-platform
   * opt-in — so resuming restores exactly what was running before.
   *
   * A timestamp rather than a boolean because "when did we stop" is the first
   * question anyone asks after pulling it.
   */
  autoPostPausedAt?: string | null;
  autoPostPausedBy?: string | null;
}

export interface PlatformConfigRecord {
  id: string;
  tenantId: string;
  reviewSettingsId: string;
  platform: ReviewPlatform;
  status: PlatformConnectionStatus;
  authType: PlatformAuthType;
  oauthTokenId: string | null;
  profileUrl: string | null;
  externalBusinessId: string | null;
  externalBusinessLabel?: string | null;
  priority: number;
  writeEnabled: boolean;
  /**
   * Auto-post opt-in for THIS platform (NOT-576 §4.3), off by default.
   * Independent of `writeEnabled`, which stays a separate gate — auto-post
   * never bypasses it.
   */
  autoPostEnabled: boolean;
  /** Who turned it on. Also the approver stamped on every resulting post. */
  autoPostEnabledBy: string | null;
  autoPostEnabledAt: string | null;
  /** The window a human gets to step in. Never zero — see §4.3. */
  autoPostDelayMinutes: number;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
  syncFrequencyHours: number;
  createdAt: string;
  updatedAt: string;
}

export interface ReviewSettingsResponse {
  settings: ReviewSettingsRecord;
  platforms: PlatformConfigRecord[];
}

// ─── DTOs ────────────────────────────────────────────────────────────────────

export interface ConnectUrlPlatformDto {
  platform: ReviewPlatform; // YELP | BBB | THUMBTACK
  profileUrl: string;
  locationId?: string;
}

export interface DisconnectPlatformDto {
  platformConfigId: string;
}

export interface ReorderPlatformsDto {
  locationId?: string;
  orderedPlatformConfigIds: string[];
}

export interface TogglePlatformFlagDto {
  platformConfigId: string;
  value: boolean;
}

export interface SetAutoPostDto {
  platformConfigId: string;
  value: boolean;
  /**
   * Omitted rather than defaulted when unchanged, so toggling auto-post off
   * and back on does not silently discard a tuned window.
   */
  delayMinutes?: number;
}

// ─── OAuth flow ──────────────────────────────────────────────────────────────

export interface OAuthAuthorizationUrlResponse {
  authorizationUrl: string;
  state: string;
}

export interface OAuthAuthorizationQuery {
  locationId?: string;
  /** Optional URL the backend should redirect to after the OAuth callback. */
  returnTo?: string;
}

export interface AvailableGoogleLocation {
  externalBusinessId: string;
  name: string;
  address?: string;
  profileUrl?: string;
  reviewCount?: number;
  averageRating?: number;
}

export interface AvailableFacebookPage {
  externalBusinessId: string;
  name: string;
  category?: string;
  reviewCount?: number;
}

export interface AvailableBusinessesResponse {
  locations?: AvailableGoogleLocation[];
  pages?: AvailableFacebookPage[];
}

export interface SelectBusinessDto {
  oauthTokenId: string;
  externalBusinessId: string;
  externalBusinessLabel?: string;
  profileUrl?: string;
  locationId?: string;
}

export interface SelectBusinessResponse {
  platformConfig: PlatformConfigRecord;
}

export interface DisconnectOAuthDto {
  oauthTokenId: string;
}
